<script setup lang="ts">
import { ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogTrigger,
    DialogScrollContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import RangeCalendarEditor from '@/ui-plus/date-time-prop-editor/RangeCalendarEditor.vue';
import { type DateRangeValueType } from '@lib/zod/dateTimePropEditor';
import StoreInvoicesList from './StoreInvoicesList.vue';
import StoreBalanceInvoiceContent from './StoreBalanceInvoiceContent.vue';

const props = defineProps<{
    storeId: string;
}>();

const dateLabel = ref('Today');
const today = new Date();
const todayStarting = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

const dateRange = ref<DateRangeValueType>({
    start: todayStarting.getTime(),
    end: Date.now(),
});

const isCustomDateRange = ref(false);
const isDraftOpen = ref(false);

const setDateRange = (days: number) => {
    dateLabel.value = days === 0 ? 'Today' : `Last ${days} days`;
    isCustomDateRange.value = false;
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    endDate.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startDate.getTime(),
        end: endDate.getTime(),
    };
};

const setThisWeek = () => {
    dateLabel.value = 'This week';
    isCustomDateRange.value = false;
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startOfWeek.getTime(),
        end: endOfWeek.getTime(),
    };
};

const setThisMonth = () => {
    dateLabel.value = 'This month';
    isCustomDateRange.value = false;
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    endOfMonth.setHours(23, 59, 59, 999);
    dateRange.value = {
        start: startOfMonth.getTime(),
        end: endOfMonth.getTime(),
    };
};

const getFormattedRange = () => {
    return `${new Date(dateRange.value.start ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.value.start ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}] - ${new Date(dateRange.value.end ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.value.end ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}]`;
};
</script>

<template>
    <div class="space-y-6">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
                <h1 class="text-2xl font-bold">Draft invoice</h1>
            </div>
            <div class="flex flex-wrap items-center gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <Button variant="outline"
                                size="sm">
                            {{ isCustomDateRange ? 'Custom date' : dateLabel }}
                            {{ getFormattedRange() }}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem @click="setDateRange(0)">
                            Today
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setDateRange(1)">
                            Last 2 days
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setDateRange(3)">
                            Last 3 days
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setDateRange(7)">
                            Last 7 days
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setDateRange(30)">
                            Last 30 days
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setDateRange(90)">
                            Last 3 months
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem @click="setThisWeek">
                            This week
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="setThisMonth">
                            This month
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <div class="px-2 py-1.5">
                            <Switch :checked="isCustomDateRange"
                                    @update:checked="isCustomDateRange = !isCustomDateRange" />
                            Custom
                            <RangeCalendarEditor v-model="dateRange"
                                                 v-if="isCustomDateRange" />
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>

                <Dialog v-model:open="isDraftOpen"
                        class="min-h-full">
                    <DialogTrigger as-child>
                        <Button variant="outline">
                            Generate
                        </Button>
                    </DialogTrigger>
                    <DialogScrollContent class="my-0 flex h-[95vh] max-h-[95vh] w-full max-w-3xl flex-col gap-2 min-h-full">
                        <DialogHeader class="shrink-0">
                            <DialogTitle>Generate Draft Invoice</DialogTitle>
                        </DialogHeader>
                        <StoreBalanceInvoiceContent v-if="props.storeId && isDraftOpen"
                                                    :key="`${dateRange.start}-${dateRange.end}`"
                                                    :store-id="props.storeId"
                                                    :start="dateRange.start"
                                                    :end="dateRange.end"
                                                    class="min-h-0 flex-1" />
                    </DialogScrollContent>
                </Dialog>
            </div>
        </div>

        <div>
            <h1 class="text-2xl font-bold">Invoices</h1>
        </div>

        <StoreInvoicesList v-if="props.storeId"
                           :store-id="props.storeId" />
    </div>
</template>
